import { Style } from '@boba-cli/chapstick';
import { newBinding, matches } from '@boba-cli/key';
import { HelpModel } from '@boba-cli/help';
import { PaginatorModel } from '@boba-cli/paginator';
import { SpinnerModel, TickMsg } from '@boba-cli/spinner';
import { batch, KeyMsg, KeyType } from '@boba-cli/tea';
import fuzzysort from 'fuzzysort';

// src/styles.ts
function defaultStyles() {
  return {
    title: new Style().bold(true),
    titleBar: new Style().underline(true),
    spinner: new Style(),
    filterPrompt: new Style(),
    filterCursor: new Style().background("#303030").foreground("#ffffff"),
    noItems: new Style().italic(true),
    statusBar: new Style().italic(true),
    statusEmpty: new Style().italic(true),
    pagination: new Style(),
    help: new Style().italic(true),
    normalTitle: new Style(),
    normalDesc: new Style().italic(true),
    selectedTitle: new Style().bold(true),
    selectedDesc: new Style(),
    dimmedTitle: new Style().italic(true),
    dimmedDesc: new Style().italic(true)
  };
}
function mergeStyles(overrides) {
  if (!overrides) {
    return defaultStyles();
  }
  return { ...defaultStyles(), ...overrides };
}

// src/delegate.ts
var DefaultDelegate = class _DefaultDelegate {
  #styles;
  constructor(styles = defaultStyles()) {
    this.#styles = styles;
  }
  height() {
    return 2;
  }
  spacing() {
    return 1;
  }
  render(item, _index, selected) {
    const titleStyle = selected ? this.#styles.selectedTitle : this.#styles.normalTitle;
    const descStyle = selected ? this.#styles.selectedDesc : this.#styles.normalDesc;
    const title = titleStyle.render(item.title());
    const desc = item.description() ? "\n" + descStyle.render(item.description()) : "";
    return `${title}${desc}`;
  }
  /** Return a copy using different styles. */
  withStyles(styles) {
    return new _DefaultDelegate(styles);
  }
};

// src/item.ts
var DefaultItem = class {
  constructor(titleText, descText = "") {
    this.titleText = titleText;
    this.descText = descText;
  }
  filterValue() {
    return this.titleText;
  }
  title() {
    return this.titleText;
  }
  description() {
    return this.descText;
  }
};
var defaultKeyMap = {
  cursorUp: newBinding({ keys: ["up", "k"] }),
  cursorDown: newBinding({ keys: ["down", "j"] }),
  prevPage: newBinding({ keys: ["left", "h", "pgup", "b", "u"] }),
  nextPage: newBinding({ keys: ["right", "l", "pgdown", "f", "d"] }),
  gotoTop: newBinding({ keys: ["home", "g"] }),
  gotoBottom: newBinding({ keys: ["end", "G"] }),
  filter: newBinding({ keys: ["/"] }),
  clearFilter: newBinding({ keys: ["esc"] }),
  acceptFilter: newBinding({
    keys: ["enter", "tab", "shift+tab", "ctrl+k", "up", "ctrl+j", "down"]
  }),
  cancelFilter: newBinding({ keys: ["esc"] }),
  showFullHelp: newBinding({ keys: ["?"] }),
  closeFullHelp: newBinding({ keys: ["?"] }),
  quit: newBinding({ keys: ["q", "esc"] }),
  forceQuit: newBinding({ keys: ["ctrl+c"] }),
  shortHelp() {
    return [
      this.cursorUp,
      this.cursorDown,
      this.prevPage,
      this.nextPage,
      this.filter,
      this.quit
    ];
  },
  fullHelp() {
    return [
      [this.cursorUp, this.cursorDown, this.gotoTop, this.gotoBottom],
      [this.prevPage, this.nextPage, this.filter, this.clearFilter],
      [this.showFullHelp, this.acceptFilter, this.cancelFilter, this.quit]
    ];
  }
};
function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}
function perPageFromHeight(height, delegate, fallback) {
  if (height <= 0) {
    return fallback;
  }
  const perItem = Math.max(1, delegate.height() + delegate.spacing());
  return Math.max(1, Math.floor(height / perItem));
}
function setPaginatorPage(paginator, page, totalItems) {
  const clampedPage = clamp(page, 0, Math.max(0, paginator.totalPages - 1));
  return PaginatorModel.new({
    type: paginator.type,
    page: clampedPage,
    perPage: paginator.perPage,
    totalPages: Math.max(1, Math.ceil(totalItems / paginator.perPage)),
    activeDot: paginator.activeDot,
    inactiveDot: paginator.inactiveDot,
    arabicFormat: paginator.arabicFormat,
    keyMap: paginator.keyMap
  });
}
function filterItems(items, query) {
  if (!query) return items;
  const targets = items.map((item) => ({
    item,
    text: item.filterValue()
  }));
  const results = fuzzysort.go(query, targets, {
    key: "text",
    threshold: -1e4
  });
  return results.map((r) => r.obj.item);
}
var ListModel = class _ListModel {
  items;
  filteredItems;
  cursor;
  filterValue;
  filterState;
  paginator;
  help;
  spinner;
  delegate;
  styles;
  keyMap;
  width;
  height;
  title;
  showTitle;
  showFilter;
  showStatusBar;
  showPagination;
  showHelp;
  filteringEnabled;
  loading;
  constructor(state) {
    this.items = state.items;
    this.filteredItems = state.filteredItems;
    this.cursor = state.cursor;
    this.filterValue = state.filterValue;
    this.filterState = state.filterState;
    this.paginator = state.paginator;
    this.help = state.help;
    this.spinner = state.spinner;
    this.delegate = state.delegate;
    this.styles = state.styles;
    this.keyMap = state.keyMap;
    this.width = state.width;
    this.height = state.height;
    this.title = state.title;
    this.showTitle = state.showTitle;
    this.showFilter = state.showFilter;
    this.showStatusBar = state.showStatusBar;
    this.showPagination = state.showPagination;
    this.showHelp = state.showHelp;
    this.filteringEnabled = state.filteringEnabled;
    this.loading = state.loading;
  }
  /** Create a new list model. */
  static new(options) {
    const styles = mergeStyles(options.styles);
    const delegate = options.delegate instanceof DefaultDelegate ? options.delegate.withStyles(styles) : options.delegate ?? new DefaultDelegate(styles);
    const items = options.items ?? [];
    const filteredItems = items;
    const perPage = perPageFromHeight(options.height ?? 0, delegate, 10);
    let paginator = PaginatorModel.new({ perPage }).setTotalPages(items.length);
    paginator = setPaginatorPage(paginator, 0, filteredItems.length);
    const width = options.width ?? 0;
    const height = options.height ?? 0;
    return new _ListModel({
      items,
      filteredItems,
      cursor: 0,
      filterValue: "",
      filterState: "unfiltered",
      paginator,
      help: HelpModel.new({ width }),
      spinner: new SpinnerModel(),
      delegate,
      styles,
      keyMap: options.keyMap ?? defaultKeyMap,
      width,
      height,
      title: options.title ?? "",
      showTitle: options.showTitle ?? true,
      showFilter: options.showFilter ?? true,
      showStatusBar: options.showStatusBar ?? true,
      showPagination: options.showPagination ?? true,
      showHelp: options.showHelp ?? true,
      filteringEnabled: options.filteringEnabled ?? true,
      loading: false
    });
  }
  /** Currently selected item (after filtering). */
  selectedItem() {
    return this.filteredItems[this.cursor];
  }
  /** Index of the selected item within the filtered collection. */
  selectedIndex() {
    return this.cursor;
  }
  /** Items visible on the current page. */
  visibleItems() {
    if (!this.showPagination) {
      return this.filteredItems;
    }
    const [start, end] = this.paginator.getSliceBounds(
      this.filteredItems.length
    );
    return this.filteredItems.slice(start, end);
  }
  /** Move cursor up by one item. */
  cursorUp() {
    if (this.filteredItems.length === 0) return this;
    const nextCursor = clamp(this.cursor - 1, 0, this.filteredItems.length - 1);
    const nextPaginator = this.showPagination ? setPaginatorPage(
      this.paginator,
      Math.floor(nextCursor / this.paginator.perPage),
      this.filteredItems.length
    ) : this.paginator;
    return this.with({ cursor: nextCursor, paginator: nextPaginator });
  }
  /** Move cursor down by one item. */
  cursorDown() {
    if (this.filteredItems.length === 0) return this;
    const nextCursor = clamp(this.cursor + 1, 0, this.filteredItems.length - 1);
    const nextPaginator = this.showPagination ? setPaginatorPage(
      this.paginator,
      Math.floor(nextCursor / this.paginator.perPage),
      this.filteredItems.length
    ) : this.paginator;
    return this.with({ cursor: nextCursor, paginator: nextPaginator });
  }
  /** Jump to first item. */
  gotoTop() {
    if (this.filteredItems.length === 0) return this;
    const nextPaginator = this.showPagination ? setPaginatorPage(this.paginator, 0, this.filteredItems.length) : this.paginator;
    return this.with({ cursor: 0, paginator: nextPaginator });
  }
  /** Jump to last item. */
  gotoBottom() {
    if (this.filteredItems.length === 0) return this;
    const last = this.filteredItems.length - 1;
    const nextPaginator = this.showPagination ? setPaginatorPage(
      this.paginator,
      Math.floor(last / this.paginator.perPage),
      this.filteredItems.length
    ) : this.paginator;
    return this.with({ cursor: last, paginator: nextPaginator });
  }
  /** Move to next page. */
  nextPage() {
    if (!this.showPagination) return this;
    const nextPaginator = this.paginator.nextPage();
    const [start] = nextPaginator.getSliceBounds(this.filteredItems.length);
    const nextCursor = clamp(
      Math.max(start, this.cursor),
      0,
      this.filteredItems.length - 1
    );
    return this.with({ paginator: nextPaginator, cursor: nextCursor });
  }
  /** Move to previous page. */
  prevPage() {
    if (!this.showPagination) return this;
    const nextPaginator = this.paginator.prevPage();
    const [start] = nextPaginator.getSliceBounds(this.filteredItems.length);
    const nextCursor = clamp(
      Math.max(start, this.cursor),
      0,
      this.filteredItems.length - 1
    );
    return this.with({ paginator: nextPaginator, cursor: nextCursor });
  }
  /** Begin filtering. */
  startFiltering() {
    if (!this.filteringEnabled) return this;
    return this.with({ filterState: "filtering" });
  }
  /** Set the filter query. */
  setFilter(value) {
    if (!this.filteringEnabled) return this;
    const filtered = filterItems(this.items, value);
    const nextPaginator = this.showPagination ? this.paginator.setTotalPages(filtered.length) : this.paginator;
    const boundedCursor = filtered.length === 0 ? 0 : clamp(this.cursor, 0, Math.max(0, filtered.length - 1));
    return this.with({
      filterValue: value,
      filterState: value ? "filtering" : "unfiltered",
      filteredItems: filtered,
      cursor: boundedCursor,
      paginator: this.showPagination ? setPaginatorPage(
        nextPaginator,
        Math.floor(boundedCursor / nextPaginator.perPage),
        filtered.length
      ) : nextPaginator
    });
  }
  /** Accept the current filter value. */
  acceptFilter() {
    if (!this.filteringEnabled) return this;
    const state = this.filterValue ? "applied" : "unfiltered";
    return this.with({ filterState: state });
  }
  /** Cancel filtering and restore the unfiltered list. */
  cancelFilter() {
    if (!this.filteringEnabled) return this;
    return this.with({
      filterValue: "",
      filterState: "unfiltered",
      filteredItems: this.items,
      cursor: clamp(this.cursor, 0, Math.max(0, this.items.length - 1)),
      paginator: this.showPagination ? this.paginator.setTotalPages(this.items.length) : this.paginator
    });
  }
  /** Clear the filter text and results. */
  clearFilter() {
    return this.cancelFilter();
  }
  /** Replace items with a new list. */
  setItems(items) {
    const filtered = filterItems(items, this.filterValue);
    const nextPaginator = this.showPagination ? this.paginator.setTotalPages(filtered.length) : this.paginator;
    const nextCursor = filtered.length === 0 ? 0 : clamp(this.cursor, 0, filtered.length - 1);
    return this.with({
      items,
      filteredItems: filtered,
      cursor: nextCursor,
      paginator: this.showPagination ? setPaginatorPage(
        nextPaginator,
        Math.floor(nextCursor / nextPaginator.perPage),
        filtered.length
      ) : nextPaginator
    });
  }
  /** Insert a single item. */
  insertItem(index, item) {
    const nextItems = [...this.items];
    nextItems.splice(clamp(index, 0, nextItems.length), 0, item);
    return this.setItems(nextItems);
  }
  /** Remove an item by index. */
  removeItem(index) {
    const nextItems = [...this.items];
    if (index < 0 || index >= nextItems.length) return this;
    nextItems.splice(index, 1);
    return this.setItems(nextItems);
  }
  /** Enter loading mode and start the spinner. */
  startLoading() {
    const model = this.with({ loading: true });
    return [model, model.spinner.tick()];
  }
  /** Stop loading. */
  stopLoading() {
    return this.with({ loading: false });
  }
  /** Update width. */
  setWidth(width) {
    return this.with({ width, help: this.help.withWidth(width) });
  }
  /** Update height and adjust pagination. */
  setHeight(height) {
    const perPage = perPageFromHeight(
      height,
      this.delegate,
      this.paginator.perPage
    );
    const nextPaginator = this.showPagination ? PaginatorModel.new({
      type: this.paginator.type,
      perPage,
      page: this.paginator.page,
      totalPages: Math.max(
        1,
        Math.ceil(this.filteredItems.length / perPage)
      ),
      activeDot: this.paginator.activeDot,
      inactiveDot: this.paginator.inactiveDot,
      arabicFormat: this.paginator.arabicFormat,
      keyMap: this.paginator.keyMap
    }) : this.paginator;
    return this.with({ height, paginator: nextPaginator });
  }
  /** Show detailed help. */
  showHelpView() {
    return this.with({ showHelp: true, help: this.help.withShowAll(true) });
  }
  /** Hide detailed help. */
  hideHelpView() {
    return this.with({ help: this.help.withShowAll(false) });
  }
  /** Tea init - start spinner if already loading. */
  init() {
    return this.loading ? this.spinner.tick() : null;
  }
  /** Handle Tea messages. */
  update(msg) {
    const cmds = [];
    if (msg instanceof TickMsg) {
      if (!this.loading) {
        return [this, null];
      }
      const [nextSpinner, spinnerCmd] = this.spinner.update(msg);
      cmds.push(spinnerCmd);
      return [this.with({ spinner: nextSpinner }), batch(...cmds)];
    }
    if (msg instanceof KeyMsg) {
      if (matches(msg, this.keyMap.cursorUp)) {
        return [this.cursorUp(), null];
      }
      if (matches(msg, this.keyMap.cursorDown)) {
        return [this.cursorDown(), null];
      }
      if (matches(msg, this.keyMap.gotoTop)) {
        return [this.gotoTop(), null];
      }
      if (matches(msg, this.keyMap.gotoBottom)) {
        return [this.gotoBottom(), null];
      }
      if (matches(msg, this.keyMap.nextPage)) {
        return [this.nextPage(), null];
      }
      if (matches(msg, this.keyMap.prevPage)) {
        return [this.prevPage(), null];
      }
      if (this.filterState !== "filtering" && matches(msg, this.keyMap.filter)) {
        return [this.startFiltering(), null];
      }
      if (matches(msg, this.keyMap.clearFilter)) {
        return [this.clearFilter(), null];
      }
      if (matches(msg, this.keyMap.acceptFilter)) {
        return [this.acceptFilter(), null];
      }
      if (matches(msg, this.keyMap.cancelFilter)) {
        return [this.cancelFilter(), null];
      }
      if (this.filterState === "filtering") {
        if ((msg.key.type === KeyType.Runes || msg.key.type === KeyType.Space) && !msg.key.alt) {
          const chars = msg.key.type === KeyType.Space ? " " : msg.key.runes;
          return [this.setFilter(this.filterValue + chars), null];
        }
        if (msg.key.type === KeyType.Backspace) {
          const newValue = this.filterValue.slice(0, -1);
          return [this.setFilter(newValue), null];
        }
      }
      if (matches(msg, this.keyMap.showFullHelp)) {
        return [this.showHelpView(), null];
      }
      if (matches(msg, this.keyMap.closeFullHelp)) {
        return [this.hideHelpView(), null];
      }
    }
    const [nextPaginator, paginatorCmd] = this.paginator.update(msg);
    if (paginatorCmd) cmds.push(paginatorCmd);
    const nextModel = nextPaginator === this.paginator ? this : this.with({ paginator: nextPaginator });
    return [nextModel, batch(...cmds)];
  }
  /** Render the list to a string. */
  view() {
    const lines = [];
    if (this.showTitle && this.title) {
      lines.push(this.styles.titleBar.render(this.title));
    }
    if (this.showFilter) {
      const prompt = this.styles.filterPrompt.render("Filter: ");
      const cursor = this.styles.filterCursor.render(this.filterValue || "_");
      lines.push(prompt + cursor);
    }
    if (this.filteredItems.length === 0) {
      lines.push(this.styles.noItems.render("No items"));
    } else {
      const visible = this.visibleItems();
      for (const [idx, item] of visible.entries()) {
        const absoluteIndex = this.showPagination ? this.paginator.getSliceBounds(this.filteredItems.length)[0] + idx : idx;
        const selected = absoluteIndex === this.cursor;
        const rendered = this.delegate.render(item, absoluteIndex, selected);
        const renderedLines = rendered.split("\n");
        for (const line of renderedLines) {
          const prefix = selected ? this.styles.selectedTitle.render("\u2502") + " " : "  ";
          lines.push(prefix + line);
        }
        if (idx < visible.length - 1 && this.delegate.spacing() > 0) {
          lines.push("");
        }
      }
    }
    if (this.showStatusBar) {
      const statusText = `${this.filteredItems.length} items`;
      const pagination = this.showPagination ? this.styles.pagination.render(this.paginator.view()) : "";
      const spinner = this.loading ? this.styles.spinner.render(this.spinner.view()) + " " : "";
      const bar = `${spinner}${statusText}${pagination ? " \u2022 " + pagination : ""}`;
      const style = this.filteredItems.length === 0 ? this.styles.statusEmpty : this.styles.statusBar;
      lines.push(style.render(bar.trim()));
    }
    if (this.showHelp) {
      lines.push(this.styles.help.render(this.help.view(this.keyMap)));
    }
    return lines.join("\n");
  }
  with(patch) {
    return new _ListModel({
      items: this.items,
      filteredItems: this.filteredItems,
      cursor: this.cursor,
      filterValue: this.filterValue,
      filterState: this.filterState,
      paginator: this.paginator,
      help: this.help,
      spinner: this.spinner,
      delegate: this.delegate,
      styles: this.styles,
      keyMap: this.keyMap,
      width: this.width,
      height: this.height,
      title: this.title,
      showTitle: this.showTitle,
      showFilter: this.showFilter,
      showStatusBar: this.showStatusBar,
      showPagination: this.showPagination,
      showHelp: this.showHelp,
      filteringEnabled: this.filteringEnabled,
      loading: this.loading,
      ...patch
    });
  }
};

export { DefaultDelegate, DefaultItem, ListModel, defaultKeyMap, defaultStyles, mergeStyles };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map